import type { CSSProperties } from 'react'
import React from 'react'
import classNames from 'classnames'

import type { Icon } from '@slite/icons'

import type { CustomNoteIconProps } from '../CustomNoteIcon/CustomNoteIcon'
import { CustomNoteIcon } from '../CustomNoteIcon/CustomNoteIcon'
import type { ButtonVariant } from './constants'
import {
  buttonSizesClasses,
  buttonSizesValues,
  buttonVariantsClasses,
} from './constants'

const BASE_CLASSES =
  'transition flexbox flexbox-align-center font-family-system flexbox-no-shrink extra-white-outline-on-focus'

type BaseButtonContentProps = {
  icon: BaseButtonProps['icon']
  iconShape?: BaseButtonProps['iconShape']
  iconColor?: BaseButtonProps['iconColor']
  iconVariant?: BaseButtonProps['iconVariant']
  content: React.ReactNode
  size: BaseButtonProps['size']
  iconClassName?: string
  iconPosition?: 'left' | 'right'
  iconSize?: BaseButtonProps['size']
  iconRadius?: 'normal' | 'full'
}

const ICON_SIZE = {
  smaller: 12,
  small: 12,
  normal: 14,
  medium: 16,
  large: 16,
}

const ICON_MARGIN_CLASS = {
  smaller: 4,
  small: 4,
  normal: 8,
  medium: 8,
  large: 8,
}

const getIconMarginClass = ({
  size = 'medium',
  iconPosition = 'left',
}: Pick<BaseButtonContentProps, 'iconPosition' | 'size'>) => {
  const marginSide = iconPosition === 'left' ? 'right' : 'left'
  return `margin-${marginSide}-${ICON_MARGIN_CLASS[size]}`
}

export const BaseButtonContent = ({
  icon: ButtonIcon,
  iconShape,
  iconColor,
  iconVariant,
  content,
  iconClassName,
  iconPosition = 'left',
  iconSize,
  size = 'medium',
}: BaseButtonContentProps) => {
  const computedIconSize = ICON_SIZE[iconSize ?? size]
  const iconMargin =
    content == null
      ? ''
      : getIconMarginClass({
          size,
          iconPosition,
        })

  const renderButtonIconComponent = () => {
    // If specified, use emoji icon as first choice
    if (iconShape) {
      return (
        <CustomNoteIcon
          className={classNames('flexbox-no-shrink', iconMargin, iconClassName)}
          iconShape={iconShape}
          iconColor={iconColor}
          iconVariant={iconVariant}
          size={computedIconSize}
        />
      )
    }

    // If no emoji icon is specified, use static icon or no icon

    if (!ButtonIcon) {
      return null
    }

    return (
      <ButtonIcon
        size={computedIconSize}
        style={{ width: computedIconSize, height: computedIconSize }}
        className={classNames('flexbox-no-shrink', iconMargin, iconClassName)}
      />
    )
  }

  return (
    <>
      {iconPosition === 'left' && renderButtonIconComponent()}
      {content}
      {iconPosition === 'right' && renderButtonIconComponent()}
    </>
  )
}

export type BaseButtonProps = {
  children: (
    outerClassName: string,
    outerStyle: React.CSSProperties,
    innerClassName: string,
    innerStyle: React.CSSProperties
  ) => React.ReactNode
  size?: 'smaller' | 'small' | 'normal' | 'medium' | 'large'
  variant?: ButtonVariant
  disabled?: boolean
  forbiddenWhenDisabled?: boolean
  className?: string
  style?: CSSProperties
  icon?: Icon | null
  iconShape?: CustomNoteIconProps['iconShape']
  iconColor?: CustomNoteIconProps['iconColor']
  iconVariant?: CustomNoteIconProps['iconVariant']
  isActive?: boolean
  iconClassName?: string
  iconPosition?: 'left' | 'right'
  iconSize?: BaseButtonProps['size']
  rounded?: boolean
  transparent?: boolean
  align?: 'left' | 'center' | 'right'
}

// Variants that need a solid background layer
const VARIANTS_WITH_SOLID_BACKGROUND: ButtonVariant[] = [
  'primary',
  'secondary',
  'secondary-blue',
  'secondary-purple',
  'destructive',
]

const ALIGN_CLASSES = {
  left: 'flexbox-justify-start',
  center: 'flexbox-justify-center',
  right: 'flexbox-justify-end',
}

export const BaseButton = ({
  variant = 'primary',
  size = 'medium',
  disabled = false,
  forbiddenWhenDisabled = true,
  isActive = false,
  className,
  children: renderChildren,
  rounded = false,
  transparent = false,
  align = 'center',
}: BaseButtonProps) => {
  // Calculate border radius based on size
  let borderRadius = 8 // default for small/medium/large
  if (rounded) {
    borderRadius = buttonSizesValues[size] / 2
  } else if (size === 'smaller') {
    borderRadius = 6
  }

  const needsSolidBackground =
    !transparent && VARIANTS_WITH_SOLID_BACKGROUND.includes(variant)

  // Outer element (button/link/label) gets solid background + border radius, no border
  const outerClassName = classNames(
    'ellipsis',
    needsSolidBackground ? 'background-shade-100' : 'background-transparent',
    className
  )

  const outerStyle = {
    border: 'none',
    borderRadius,
    minHeight: buttonSizesValues[size],
  }

  // Inner wrapper div gets all the button styling (including borders)
  const innerClassName = classNames(
    BASE_CLASSES,
    size === 'smaller' ? 'font-size-normal' : 'font-size-large',
    ALIGN_CLASSES[align],
    buttonVariantsClasses[variant]?.base,
    buttonSizesClasses[size],
    {
      // default
      pointer: !disabled,
      [buttonVariantsClasses[variant]?.default || '']: !disabled && !isActive,

      // disabled
      'cursor-forbidden': disabled && forbiddenWhenDisabled,
      [buttonVariantsClasses[variant]?.disabled || '']: disabled && !isActive,
    },
    {
      // isActive
      [buttonVariantsClasses[variant]?.isActive || '']: isActive && !disabled,
    }
  )

  const innerStyle = { minHeight: buttonSizesValues[size], borderRadius }

  return (
    <>
      {renderChildren(outerClassName, outerStyle, innerClassName, innerStyle)}
    </>
  )
}
